import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { ageFromBirthday } from '../utils/hobbies'

const WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const MIN_YEAR = 1920

const pad = (n) => String(n).padStart(2, '0')
const toIso = (year, month, day) => `${year}-${pad(month + 1)}-${pad(day)}`
const parseIso = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''))
  if (!match) return null
  return { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) }
}

const formatBirthday = (iso) => {
  const parsed = parseIso(iso)
  if (!parsed) return ''
  return `${MONTHS[parsed.month].slice(0, 3)} ${parsed.day}, ${parsed.year}`
}

const SettingsCalendar = ({ value, onChange, placeholder }) => {
  const today = new Date()
  const todayIso = toIso(today.getFullYear(), today.getMonth(), today.getDate())
  const selected = parseIso(value)
  const wrapRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState(() => selected || { year: today.getFullYear() - 18, month: today.getMonth() })
  const maxYear = today.getFullYear()
  const years = useMemo(() => {
    const list = []
    for (let year = maxYear; year >= MIN_YEAR; year -= 1) list.push(year)
    return list
  }, [maxYear])

  useEffect(() => {
    if (!open) return undefined
    if (selected) setCursor({ year: selected.year, month: selected.month })
    const close = (event) => {
      if (event.target.closest?.('select, option')) return
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  const cells = useMemo(() => {
    const first = new Date(cursor.year, cursor.month, 1).getDay()
    const count = new Date(cursor.year, cursor.month + 1, 0).getDate()
    const list = Array.from({ length: first }, () => null)
    for (let day = 1; day <= count; day += 1) list.push(day)
    while (list.length % 7) list.push(null)
    return list
  }, [cursor.year, cursor.month])

  const clampYear = (year) => Math.min(maxYear, Math.max(MIN_YEAR, year))

  const shift = (delta) => {
    setCursor(current => {
      const date = new Date(current.year, current.month + delta, 1)
      return { year: clampYear(date.getFullYear()), month: date.getFullYear() < MIN_YEAR ? 0 : date.getMonth() }
    })
  }

  const label = value ? `${formatBirthday(value)}${ageFromBirthday(value) ? ` · ${ageFromBirthday(value)} yrs` : ''}` : ''

  return (
    <div className="settingsCalWrap" ref={wrapRef}>
      <button type="button" className={`settingsCalInput${open ? ' isOpen' : ''}${value ? '' : ' isEmpty'}`} onClick={() => setOpen(current => !current)}>
        <span>{label || placeholder || 'Pick your birthday'}</span>
        <FiCalendar />
      </button>
      {open ? (
        <div className="settingsCal">
          <div className="settingsCalHead">
            <button type="button" onClick={() => shift(-1)} aria-label="Previous month"><FiChevronLeft /></button>
            <div className="settingsCalPickers">
              <label className="settingsCalSelect">
                <span className="srOnly">Month</span>
                <select
                  value={cursor.month}
                  onMouseDown={event => event.stopPropagation()}
                  onChange={event => setCursor(current => ({ ...current, month: Number(event.target.value) }))}
                >
                  {MONTHS.map((month, index) => (
                    <option key={month} value={index}>{month.slice(0, 3)}</option>
                  ))}
                </select>
              </label>
              <label className="settingsCalSelect">
                <span className="srOnly">Year</span>
                <select
                  value={cursor.year}
                  onMouseDown={event => event.stopPropagation()}
                  onChange={event => setCursor(current => ({ ...current, year: Number(event.target.value) }))}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </label>
            </div>
            <button type="button" onClick={() => shift(1)} aria-label="Next month"><FiChevronRight /></button>
          </div>
          <div className="settingsCalWeek">
            {WEEK.map(day => <span key={day}>{day}</span>)}
          </div>
          <div className="settingsCalGrid">
            {cells.map((day, index) => {
              if (!day) return <span key={`e-${index}`} className="settingsCalEmpty" />
              const iso = toIso(cursor.year, cursor.month, day)
              const disabled = iso > todayIso || iso < `${MIN_YEAR}-01-01`
              const isSelected = value === iso
              const isToday = todayIso === iso
              return (
                <button
                  key={iso}
                  type="button"
                  disabled={disabled}
                  className={`settingsCalDay${isSelected ? ' isSelected' : ''}${isToday ? ' isToday' : ''}`}
                  onClick={() => {
                    onChange(iso)
                    setOpen(false)
                  }}
                >
                  {day}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default SettingsCalendar
